/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

const NAVY = "#1a365d";
const ACCENT_BLUE = "#2b6cb0";
const TEXT_MAIN = "#333333";
const TEXT_BODY = "#555555";
const TEXT_MUTED = "#4a5568";
const BG_PAGE = "#f4f7f6";
const BG_CARD = "#ffffff";
const BORDER = "#e2e8f0";
const SUMMARY_BG = "#f8fafc";
const WARNING_TEXT = "#e53e3e";
const WARNING_BG = "#fff5f5";
const FOOTER_BG = "#f8fafc";
const FOOTER_MUTED = "#a0aec0";

export function escapeHtml(value) {
  return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
}

export function formatDateDMY(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatPaymentLabel(data) {
  if (!data) return "Payment";
  if (data?.is_supplement || data?.isSupplement) {
    if (data.supplement_sequence) {
      return `Add-on Supplement #${data.supplement_sequence}`;
    }
    return data.payment_mode || "Add-on Supplement";
  }
  if (data.payment_mode === "Installment" && data.installment_number) {
    return `Installment ${data.installment_number}`;
  }
  return data.payment_mode || "Payment";
}

export function formatPaymentAmount(data) {
  const currency = (data?.currency || "").trim();
  const amount = Number(data?.amount || 0).toFixed(2);
  return currency ? `${currency} ${amount}` : amount;
}

/** Payment emails show company name only — no header logo. */
export function resolveLogoUrl() {
  return "";
}

/**
 * Classifies the payment into one of three flows.
 * - "full": pay the entire order in one go, nothing left after this
 * - "deposit_balance": deposit now, single remaining balance due later
 * - "installment": deposit/first payment now, multiple future installments
 *
 * Driven off `payment_mode` - adjust the substring matches below if your
 * backend uses different literal values.
 */
export function resolvePaymentType(data) {
  if (data?.is_supplement || data?.isSupplement) return "full";
  const mode = String(data?.payment_mode || "").toLowerCase();
  if (mode.includes("install")) return "installment";
  if (mode.includes("deposit") || mode.includes("balance")) return "deposit_balance";
  return "full";
}

function formatMoney(amount, currency) {
  const n = Number(amount || 0).toFixed(2);
  return currency ? `${currency} ${n}` : n;
}

export function formatDateShort(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function parseDateLoose(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + Number(days));
  return d;
}

/**
 * Trip departure date, checked across the field names this data shape
 * commonly appears under. Needed to derive "30 days before departure" style
 * due dates when a schedule row has no explicit due_date.
 */
export function getDepartureDate(data) {
  return parseDateLoose(
      data?.departure_date || data?.trip_departure_date || data?.trip?.departure_date || data?.departureDate
  );
}

/**
 * Resolves a due date for a schedule row (or the top-level payment data).
 * Prefers an explicit due_date/due_at. Falls back to computing it from
 * days_before_departure (e.g. "15 days before departure") against the trip's
 * departure date, when both are available.
 */
function resolveDueDate(source, departureDate) {
  const explicit = parseDateLoose(source?.due_date || source?.due_at || source?.payment_due_date);
  if (explicit) return explicit;
  const daysBefore = source?.days_before_departure ?? source?.due_days_before_departure;
  if (daysBefore != null && departureDate) {
    return addDays(departureDate, -Number(daysBefore));
  }
  return null;
}

/**
 * Due date for the payment this link/email is currently for. Checks the
 * top-level payment data first (direct due_date, or its own
 * days_before_departure), then falls back to the matching row in `schedule`
 * if one exists there instead.
 */
export function getCurrentDueDate(data) {
  const departureDate = getDepartureDate(data);
  const direct = resolveDueDate(data, departureDate);
  if (direct) return direct;
  const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
  const currentRow = schedule.find((row) => isSameRow(row, data));
  return currentRow ? resolveDueDate(currentRow, departureDate) : null;
}

/**
 * Total order/invoice amount, independent of what's payable right now.
 * Falls back to summing the schedule, then to the single payable amount,
 * if the backend doesn't send total_amount/order_total directly.
 */
export function getTotalAmount(data) {
  if (data?.is_supplement || data?.isSupplement) {
    return Number(data?.amount ?? data?.total_amount ?? 0);
  }
  if (data?.total_amount != null) return Number(data.total_amount);
  if (data?.order_total != null) return Number(data.order_total);
  const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
  if (schedule.length) {
    return schedule.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  }
  return Number(data?.amount || 0);
}

/**
 * Robust "is this schedule row settled" check. The original bug: this only
 * matched `row.status === "paid"` (exact-case string, single field), so a
 * deposit row coming back as e.g. `status: "Paid"`, `payment_status: "succeeded"`,
 * or with no status field at all but `balance_due: 0` was falling through and
 * getting listed as a future/unpaid balance even though it was already settled.
 *
 * This checks several common shapes and is intentionally permissive — adjust
 * PAID_STATUS_VALUES or add a field check here to match your actual API
 * response exactly (see note at the bottom of this file).
 */
const PAID_STATUS_VALUES = new Set([
  "paid", "completed", "success", "succeeded", "settled", "captured", "paid_full",
]);

function isRowPaid(row) {
  if (!row) return false;
  if (row.paid === true || row.is_paid === true) return true;
  const status = String(row.status ?? row.payment_status ?? "").trim().toLowerCase();
  if (PAID_STATUS_VALUES.has(status)) return true;
  if (row.balance_due != null && Number(row.balance_due) <= 0) return true;
  if (row.remaining_amount != null && Number(row.remaining_amount) <= 0) return true;
  if (row.amount_paid != null && row.amount != null && Number(row.amount_paid) >= Number(row.amount)) return true;
  return false;
}

function isSameRow(row, data) {
  if (row.is_current === true) return true;
  const currentId = data?.invoice_id ?? data?.id;
  if (currentId != null && (row.invoice_id === currentId || row.id === currentId)) return true;
  const currentInvoiceNumber = data?.invoice_number;
  if (currentInvoiceNumber && row.invoice_number != null && String(row.invoice_number) === String(currentInvoiceNumber)) {
    return true;
  }
  return false;
}

function mapScheduleRow(row, data, departureDate) {
  const dueDate = resolveDueDate(row, departureDate);
  return {
    label: row.installment_number ? `Installment ${row.installment_number}` : row.label || "Balance due",
    amount: Number(row.amount || 0),
    currency: row.currency || data?.currency || "",
    dueDate: dueDate ? dueDate.toISOString() : "",
    status: row.status || (isRowPaid(row) ? "paid" : "pending"),
  };
}

/**
 * Schedule rows already settled, excluding the row this link/email is for.
 * Used to show "already paid" separately from what's still upcoming.
 */
export function getPaidRows(data) {
  if (data?.is_supplement || data?.isSupplement) return [];
  const departureDate = getDepartureDate(data);
  const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
  return schedule
      .filter((row) => !isSameRow(row, data) && isRowPaid(row))
      .sort((a, b) => new Date(a.due_date || a.due_at || a.payment_due_date || 0) - new Date(b.due_date || b.due_at || b.payment_due_date || 0))
      .map((row) => mapScheduleRow(row, data, departureDate));
}

/**
 * Remaining/unpaid future rows from the schedule — excludes both the invoice
 * this link is currently for AND any row already settled (e.g. a paid deposit).
 * Used for both the "final balance" case (deposit_balance, single row) and
 * the installment case (multiple rows). Each row's due date is resolved from
 * an explicit due_date or, failing that, departure_date minus
 * days_before_departure.
 */
export function getFutureInstallments(data) {
  if (data?.is_supplement || data?.isSupplement) return [];
  const departureDate = getDepartureDate(data);
  const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
  return schedule
      .filter((row) => !isSameRow(row, data) && !isRowPaid(row))
      .sort((a, b) => new Date(a.due_date || a.due_at || a.payment_due_date || 0) - new Date(b.due_date || b.due_at || b.payment_due_date || 0))
      .map((row) => mapScheduleRow(row, data, departureDate));
}

/**
 * Travel payment links have a travel_order_id; legacy CRM / sales-order invoices do not.
 */
export function isTravelPaymentLink(data) {
  return Boolean(data?.travel_order_id);
}

/**
 * Bundles everything the email/preview need to render the payment-type-aware
 * summary block: totals, what's already paid, what's payable now, what's
 * still upcoming, and a context note.
 */
export function buildPaymentSummaryContext(data) {
  const isSupplement = Boolean(data?.is_supplement || data?.isSupplement);
  const paymentType = resolvePaymentType(data);
  const currency = (data?.currency || "").trim();
  const totalAmount = getTotalAmount(data);
  const paidRows = getPaidRows(data);
  const futureInstallments = getFutureInstallments(data);
  const paidSoFar = paidRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const remainingAfterThis = futureInstallments.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const currentDueDate = getCurrentDueDate(data);
  const departureDate = getDepartureDate(data);
  const isTravel = isTravelPaymentLink(data);
  const ctx = {
    paymentType,
    isTravel,
    isSupplement,
    emailVariant: isSupplement ? "supplement" : isTravel ? "travel" : "legacy",
    totalAmountFormatted: formatMoney(totalAmount, currency),
    payableAmountFormatted: formatPaymentAmount(data),
    paidRows,
    paidSoFarFormatted: paidSoFar > 0 ? formatMoney(paidSoFar, currency) : "",
    futureInstallments,
    remainingAfterThisFormatted: remainingAfterThis > 0 ? formatMoney(remainingAfterThis, currency) : "",
    orderNumber: data?.order_number || "",
    invoiceNumber: data?.invoice_number || "",
    tripPackageName: data?.trip_package_name || "",
    tripDepartureDate: formatDateDMY(data?.trip_departure_date),
    travellers: data?.travellers || "",
    currentDueDateFormatted: currentDueDate ? formatDateShort(currentDueDate.toISOString()) : "",
    departureDateFormatted: departureDate ? formatDateShort(departureDate.toISOString()) : "",
    supplementSequence: data?.supplement_sequence || null,
  };
  const contextNote = isSupplement
    ? "Payment for add-on supplement. Billed separately from your main booking payment schedule."
    : isTravel
    ? "Please complete the required payment to secure your booking."
    : "Please complete payment for this invoice by the due date.";
  return { ...ctx, contextNote };
}

function paymentTypeChipLabel(paymentType, paymentLabel, isSupplement = false) {
  if (isSupplement) return paymentLabel || "Add-on Supplement";
  if (paymentType === "installment") return paymentLabel; // e.g. "Installment 2"
  if (paymentType === "deposit_balance") return "Deposit";
  return "Full payment";
}

function buildFutureInstallmentsHtml(futureInstallments) {
  if (!futureInstallments?.length) return "";
  const rows = futureInstallments
      .map((row) => {
        const dueLabel = row.dueDate ? ` &middot; due ${escapeHtml(formatDateShort(row.dueDate))}` : "";
        return `
        <tr>
          <td style="padding-bottom:8px;font-size:15px;color:${TEXT_MUTED};">
            ${escapeHtml(row.label)}<span style="color:#94a3b8;font-size:12px;">${dueLabel}</span>
          </td>
          <td style="padding-bottom:8px; text-align:right;font-size:15px;color:${TEXT_MUTED};">
            ${escapeHtml(formatMoney(row.amount, row.currency))}
          </td>
        </tr>`;
      })
      .join("");

  const heading = futureInstallments.length > 1 ? "Upcoming Installments" : "Remaining Balance";

  return `
    <div style="background-color: ${SUMMARY_BG}; border: 1px solid ${BORDER}; border-radius: 6px; padding: 20px; margin-bottom: 30px;">
      <h3 style="margin-top: 0; margin-bottom: 15px; font-size: 18px; color: ${NAVY}; border-bottom: 2px solid ${BORDER}; padding-bottom: 10px;">${heading}</h3>
      <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; font-size: 15px; color: ${TEXT_MUTED}; line-height: 1.8;">
        ${rows}
      </table>
    </div>`;
}

/**
 * Branded HTML payment-request email - matches PearlCRM theme (info blue #1565C8).
 *
 * `summary` (optional) is the object returned by buildPaymentSummaryContext.
 * When omitted, the email renders exactly as before (backward compatible).
 */
export function buildPaymentLinkEmailHtml({
  customerName = "",
  paymentLabel = "Payment",
  amountFormatted = "",
  invoiceNumber = "",
  paymentUrl = "",
  companyName = "PearlCRM",
  logoUrl = "",
  gateway = "stripe",
  paymentType = "",
  personalNote = "",
  year = new Date().getFullYear(),
  summary = null,
}) {
  // Travel booking copy only when the payment link is tied to a travel order.
  const useTravelCopy = Boolean(summary?.isTravel);

  const safeName = escapeHtml(customerName);
  const safeLabel = escapeHtml(paymentLabel);
  const safeAmount = escapeHtml(amountFormatted);
  const safeInvoice = escapeHtml(invoiceNumber || summary?.invoiceNumber || "");
  const safeUrl = escapeHtml(paymentUrl);
  const safeCompany = escapeHtml(companyName);
  const safeLogo = escapeHtml(logoUrl);

  const noteBlock = personalNote?.trim()
      ? `<p style="margin:0 0 20px;font-size:15px;line-height:1.6;color:${TEXT_MAIN};">${escapeHtml(personalNote.trim()).replace(/\n/g, "<br/>")}</p>`
      : "";

  const safeTotal = escapeHtml(summary?.totalAmountFormatted || "");
  const safePaidSoFar = escapeHtml(summary?.paidSoFarFormatted || "");
  const safeRemainingAfter = escapeHtml(summary?.remainingAfterThisFormatted || "");
  const safeDueDate = escapeHtml(summary?.currentDueDateFormatted || "");
  const isSupplement = Boolean(summary?.isSupplement);
  const safeChip = escapeHtml(
      summary ? paymentTypeChipLabel(summary.paymentType, paymentLabel, isSupplement) : safeLabel
  );
  const safeContextNote = escapeHtml(summary?.contextNote || "");
  const futureInstallmentsHtml = summary ? buildFutureInstallmentsHtml(summary.futureInstallments) : "";

  const totalAmountLabel = isSupplement
      ? "Supplement Total:"
      : useTravelCopy
      ? "Total Order Amount:"
      : "Invoice Amount:";
  const totalAmountRow =
      summary && safeTotal
          ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>${totalAmountLabel}</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeTotal}</td>
      </tr>`
          : "";

  const paidSoFarRow = safePaidSoFar
      ? `
      <tr>
        <td style="padding-bottom: 8px; color: #2f855a;"><strong>Already Paid:</strong></td>
        <td style="padding-bottom: 8px; text-align: right; color: #2f855a;">${safePaidSoFar}</td>
      </tr>`
      : "";

  const remainingAfterRow = safeRemainingAfter
      ? `
      <tr>
        <td style="padding-top: 8px; font-size: 13px; vertical-align: top; white-space: nowrap;"><strong>Remaining after this payment:</strong></td>
        <td style="padding-top: 8px; text-align: right; font-size: 13px; vertical-align: top; white-space: nowrap;"><strong>${safeRemainingAfter}</strong></td>
      </tr>`
      : "";

  const payBeforeRow = safeDueDate
      ? `
      <tr>
        <td colspan="2" style="padding-top: 10px; font-size: 13px; font-weight: 700; color: ${WARNING_TEXT};">
          Please pay before ${safeDueDate}
        </td>
      </tr>`
      : "";

  const safeOrderNumber = escapeHtml(summary?.orderNumber || "");
  const orderRow =
      useTravelCopy && safeOrderNumber
          ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Order:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeOrderNumber}</td>
      </tr>`
          : !useTravelCopy && safeOrderNumber
            ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Order:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeOrderNumber}</td>
      </tr>`
            : "";

  const invoiceRow = safeInvoice
      ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Invoice:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeInvoice}</td>
      </tr>`
      : "";

  const safeTripName = escapeHtml(summary?.tripPackageName || "");
  const tripRow =
      useTravelCopy && safeTripName
          ? `
      <tr>
        <td style="padding-bottom: 8px; vertical-align: top; white-space: nowrap;"><strong>Trip Package:</strong></td>
        <td style="padding-bottom: 8px; vertical-align: top; text-align: right; word-break: break-word;">${safeTripName}</td>
      </tr>`
          : "";

  const safeDepartureDate = escapeHtml(summary?.tripDepartureDate || "");
  const departureDateRow =
      useTravelCopy && safeDepartureDate
          ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Departure Date:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeDepartureDate}</td>
      </tr>`
          : "";

  const safeTravellers = escapeHtml(summary?.travellers != null ? String(summary.travellers) : "");
  const travelerRow =
      useTravelCopy && safeTravellers
          ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Travelers:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeTravellers}</td>
      </tr>`
          : "";

  const PaymentTypeRow = safeChip
      ? `
      <tr>
        <td style="padding-bottom: 8px;"><strong>Payment Type:</strong></td>
        <td style="padding-bottom: 8px; text-align: right;">${safeChip}</td>
      </tr>`
      : "";

  const detailsHeading = isSupplement
      ? "Add-on Supplement Details"
      : useTravelCopy
      ? "Booking Details"
      : "Invoice Details";
  const summaryBlock = `
    <div style="background-color: ${SUMMARY_BG}; border: 1px solid ${BORDER}; border-radius: 6px; padding: 20px; margin-bottom: 30px;">
      <h3 style="margin-top: 0; margin-bottom: 15px; font-size: 18px; color: ${NAVY}; border-bottom: 2px solid ${BORDER}; padding-bottom: 10px;">${detailsHeading}</h3>
      <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; font-size: 15px; color: ${TEXT_MUTED}; line-height: 1.5;">
      ${useTravelCopy ? orderRow : invoiceRow}
      ${useTravelCopy ? tripRow : ""}
      ${useTravelCopy ? departureDateRow : ""}
      ${useTravelCopy ? travelerRow : ""}
      ${useTravelCopy ? "" : orderRow}
      ${PaymentTypeRow}
      ${paidSoFarRow}
      ${totalAmountRow}
        <tr>
          <td style="padding-top: 10px; border-top: 1px dashed #cbd5e0; vertical-align: top;"><strong>Amount Payable Now:</strong></td>
          <td style="padding-top: 10px; border-top: 1px dashed #cbd5e0; text-align: right; vertical-align: top; color: ${ACCENT_BLUE}; font-weight: bold; font-size: 16px; white-space: nowrap;">${safeAmount}</td>
        </tr>
        ${payBeforeRow}
        ${remainingAfterRow}
      </table>
    </div>`;

  const contextNoteBlock = safeContextNote
      ? `
      <p style="font-size: 15px; line-height: 1.6; color: ${WARNING_TEXT}; background-color: ${WARNING_BG}; padding: 15px; border-left: 4px solid ${WARNING_TEXT}; margin-bottom: 30px;">
        ${safeContextNote}
      </p>`
      : "";

  const introParagraph = isSupplement
      ? `An add-on supplement has been added to your booking${safeOrderNumber ? ` (<strong>${safeOrderNumber}</strong>)` : ""} with <strong>${safeCompany}</strong> and is awaiting payment.`
      : useTravelCopy
      ? `Your booking with <strong>${safeCompany}</strong> has been successfully created and is awaiting payment.`
      : `Your invoice from <strong>${safeCompany}</strong>${safeInvoice ? ` (<strong>${safeInvoice}</strong>)` : ""} is ready for payment.`;

  const afterCtaParagraph = isSupplement
      ? `Your add-on services will be confirmed once payment is received. Your existing booking and payment schedule remain unchanged.`
      : useTravelCopy
      ? `Your booking will be secured once the payment is received. If payment is not received by the due date, the booking may be automatically cancelled as per the applicable booking terms.`
      : `Your payment will be applied to this invoice once received. If you have already paid, please disregard this message.`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Payment Request</title>
    <style>
        body, p, h1, h2, h3, h4, h5, h6 { margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background-color: ${BG_PAGE}; color: ${TEXT_MAIN}; -webkit-font-smoothing: antialiased; }
        table { border-collapse: collapse; width: 100%; }
        @media screen and (max-width: 600px) {
            .container { width: 100% !important; }
            .content-padding { padding: 20px !important; }
        }
    </style>
</head>
<body style="background-color: ${BG_PAGE}; padding: 20px 0;">

<table align="center" border="0" cellpadding="0" cellspacing="0" class="container" style="background-color: ${BG_CARD}; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">

    <!-- Header -->
    <tr>
        <td align="center" style="background-color: ${NAVY}; padding: 30px 20px; text-align: center;">
            ${safeLogo ? `<img src="${safeLogo}" alt="${safeCompany}" width="80" style="display:block;margin:0 auto 12px;max-width:120px;height:auto;border:0;" />` : ""}
            <h2 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 600; letter-spacing: 1px;">${safeCompany}</h2>
        </td>
    </tr>

    <!-- Body -->
    <tr>
        <td class="content-padding" style="padding: 40px 40px 20px 40px;">
            <p style="font-size: 16px; line-height: 1.6; color: ${TEXT_MAIN}; margin-bottom: 20px;">
                ${safeName ? `Dear <strong>${safeName}</strong>,` : "Dear Customer,"}
            </p>
            <p style="font-size: 16px; line-height: 1.6; color: ${TEXT_BODY}; margin-bottom: 30px;">
                ${introParagraph}
            </p>

            ${noteBlock}

            ${summaryBlock}

            ${contextNoteBlock}

            <!-- CTA -->
            <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-bottom: 30px;">
                <tr>
                    <td align="center">
                        <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; background-color: ${ACCENT_BLUE}; color: #ffffff; text-decoration: none; padding: 14px 28px; font-size: 16px; font-weight: bold; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">Complete Payment</a>
                    </td>
                </tr>
            </table>

            <p style="font-size: 13px; line-height: 1.5; color: ${TEXT_MUTED}; margin-bottom: 8px;">
                ${afterCtaParagraph}
            </p>

            <p style="font-size: 13px; line-height: 1.5; color: ${TEXT_MUTED}; margin-bottom: 8px;">
                <strong>If the button does not work, copy and paste this link into your browser:</strong>
            </p>
            <p style="font-size: 12px; line-height: 1.5; word-break: break-all; margin-bottom: 20px;">
                <a href="${safeUrl}" style="color: ${ACCENT_BLUE}; text-decoration: underline;">${safeUrl}</a>
            </p>

            ${futureInstallmentsHtml}

            <p style="font-size: 15px; line-height: 1.6; color: ${TEXT_BODY}; margin-bottom: 10px;">
                If you need any assistance, please contact our support team.
            </p>
        </td>
    </tr>

    <!-- Footer -->
    <tr>
        <td class="content-padding" style="background-color: ${FOOTER_BG}; padding: 30px 40px; border-top: 1px solid ${BORDER};">
            <p style="font-size: 15px; color: ${TEXT_MUTED}; margin-bottom: 5px;">Regards,</p>
            <p style="font-size: 15px; font-weight: bold; color: ${NAVY};">${safeCompany} Team</p>

            <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 20px;">
                <tr>
                    <td style="font-size: 12px; color: ${FOOTER_MUTED}; text-align: center;">
                        <p style="margin-bottom: 5px;">&copy; ${year} ${safeCompany}. All rights reserved.</p>
                        <p>This is an automated email from our system. Please do not reply directly to this address.</p>
                        <p style="margin-top: 10px;">Powered by <a href="https://www.pearlcrm.com/" target="_blank" rel="noopener noreferrer" style="color: ${ACCENT_BLUE}; text-decoration: underline;">PearlCRM</a></p>
                    </td>
                </tr>
            </table>
        </td>
    </tr>

</table>

</body>
</html>`;
}

export function buildPaymentLinkEmail(data, company = {}, options = {}) {
  const paymentLabel = formatPaymentLabel(data);
  const amountFormatted = formatPaymentAmount(data);
  const logoUrl = "";
  const companyName = company.company_name || company.name || "PearlCRM";
  const summary = buildPaymentSummaryContext(data);
  const invoiceNumber = data?.invoice_number || "";

  const content = buildPaymentLinkEmailHtml({
    customerName: data?.customer_name || "",
    paymentLabel,
    amountFormatted,
    invoiceNumber,
    paymentUrl: data?.payment_url || "",
    companyName,
    logoUrl,
    gateway: data?.gateway || "stripe",
    personalNote: options.personalNote || "",
    year: new Date().getFullYear(),
    summary,
  });

  const subject = summary.isSupplement
    ? `Payment request: ${paymentLabel}${data?.order_number ? ` - Order ${data.order_number}` : (data?.trip_package_name ? ` - ${data.trip_package_name}` : "")}`
    : summary.isTravel
    ? `Complete your payment request${data?.trip_package_name ? ` - ${data.trip_package_name}` : ""}`
    : `Payment request — Invoice ${invoiceNumber || ""}`.trim();

  return {
    to: data?.customer_email || "",
    subject,
    content,
    paymentLabel,
    amountFormatted,
    logoUrl,
    companyName,
    summary,
  };
}